import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  NgZone,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
  ConfigurationService,
  TimezoneService,
} from '@sc/data-access-configuration';
import {
  Appointment,
  CLIENT_APP_ID,
  ClientAppId,
  Fitter,
} from '@sc/data-access-models';
import { SandboxService } from '@sc/data-access-state-management';
import { LocaleDatePipe, TranslatorService } from '@sc/data-access-translation';
import { FocusManagerService } from '@sc/feature-focus-manager';
import { AppointmentUtils, dayjs, isTimeBetween } from '@sc/utils';
import { throttle } from 'lodash-es';
import { Subject, of } from 'rxjs';
import { debounceTime, delay } from 'rxjs/operators';
import { MomentParseZonePipe } from '../../../pipes';
import { ButtonComponent } from '../../button';
import { IconComponent } from '../../icon';

@Component({
  selector: 'sc-appointment-common-time-lines',
  styleUrls: ['./appointment-common-time-lines.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    NgIf,
    NgFor,
    NgClass,
    IconComponent,
    ButtonComponent,
    MomentParseZonePipe,
    TranslateModule,
    LocaleDatePipe,
    AsyncPipe,
  ],
  template: `
    <div
      *ngFor="let aptLines of linesDisplayed; index as lineIndex"
      class="slots-line-container"
      [id]="'time-line-' + lineIndex"
    >
      <div *ngIf="lines.length" class="slots-line">
        <div class="slots-type">
          <div
            class="slot-type"
            *ngIf="
              isLineHasAppointmentAtGarage(lines[aptLines]) &&
              displayGarageAppointments
            "
          >
            <sc-icon
              name="fitter"
              specificName="search_fitter"
              [forceInitialColor]="true"
            ></sc-icon>
          </div>
          <div
            class="slot-type"
            *ngIf="
              lines.length &&
              isLineHasAppointmentAtHome(lines[aptLines]) &&
              displayHomeAppointments &&
              !displayHomeAppointmentsAsHalfDay
            "
          >
            <sc-icon
              name="home"
              specificName="search_home"
              [forceInitialColor]="true"
            ></sc-icon>
          </div>
        </div>
        <ol class="slot-list-container" [attr.aria-labelledby]="ariaLabelledby">
          <li
            *ngFor="
              let appointmentsAvailable of lines[aptLines];
              index as slotIndex
            "
          >
            <div
              class="slots-datetime"
              [class.is-first-slot-col]="slotIndex === 0"
            >
              <ng-container
                *ngIf="
                  isLineHasAppointmentAtGarage(lines[aptLines]) &&
                  displayGarageAppointments
                "
              >
                <h4 class="inline-slot-label sc-design-text-4-bold">
                  {{
                    'CustomerDeclaration.AppointmentSearchPage.fitterIntervention'
                      | translate
                  }}
                </h4>
                <div
                  class="slot-container garage-slot-container"
                  [style.min-width]="appointmentsAvailable.slotWidth + 'px'"
                  [class.hidden-slot]="
                    !appointmentsAvailable.garage &&
                    !displayHomeAppointmentsAsHalfDay
                  "
                  [class.removed-slot]="
                    !appointmentsAvailable.garage &&
                    displayHomeAppointmentsAsHalfDay
                  "
                  *ngIf="
                    isLineHasAppointmentAtGarage(lines[aptLines]) &&
                    displayGarageAppointments
                  "
                >
                  <div
                    [id]="getSlotId(appointmentsAvailable.garage)"
                    class="slot slot-garage sc-design-title-card"
                    tabindex="0"
                    role="button"
                    [attr.data-testid]="'appointment-slot'"
                    [attr.aria-label]="
                      'GlobalApplicativeConstants.accessibility.labels.appointmentSlotBtn'
                        | translate
                          : {
                              apmtTime:
                                appointmentsAvailable.garage?.time
                                | parseZoneTimeString
                                  : timezoneService.getTimezone(
                                      appointmentsAvailable.garage
                                    )
                                | localeDate: 'shortTime'
                                | async,
                              withRv:
                                !!appointmentsAvailable.garage
                                  ?.replacementVehicle
                            }
                    "
                    (click)="onSlotClick(appointmentsAvailable.garage)"
                    (keydown.enter)="onSlotClick(appointmentsAvailable.garage)"
                    (keydown.space)="onSlotClick(appointmentsAvailable.garage)"
                    *ngIf="
                      displayOnlyReplacementVehicle
                        ? appointmentsAvailable.garage &&
                          appointmentsAvailable.garage.replacementVehicle
                        : appointmentsAvailable.garage
                    "
                    [ngClass]="{
                      selected:
                        !!selectedAppointment &&
                        appointmentsAvailable.garage?.id ===
                          selectedAppointment?.id
                    }"
                  >
                    <span class="slot-text">
                      {{
                        appointmentsAvailable.garage?.time
                          | parseZoneTimeString
                            : timezoneService.getTimezone(
                                appointmentsAvailable.garage
                              )
                          | localeDate: 'shortTime'
                          | async
                      }}
                    </span>
                    <span
                      class="replacement-vehicle-icon"
                      *ngIf="
                        appointmentsAvailable.garage.replacementVehicle &&
                        displayReplacementVehicle
                      "
                    >
                      <sc-icon
                        name="car"
                        specificName="replacement_vehicle"
                        [forceInitialColor]="true"
                      ></sc-icon>
                    </span>
                    <span class="on-select-icon" *ngIf="!!selectedAppointment">
                      <sc-icon name="check_bis"></sc-icon>
                    </span>
                  </div>
                </div>
              </ng-container>

              <ng-container
                *ngIf="
                  isLineHasAppointmentAtHome(lines[aptLines]) &&
                  displayHomeAppointments &&
                  !displayHomeAppointmentsAsHalfDay &&
                  !displayOnlyReplacementVehicle
                "
              >
                <h4 class="inline-slot-label sc-design-text-4-bold">
                  {{
                    'CustomerDeclaration.AppointmentSearchPage.outsideIntervention'
                      | translate
                  }}
                </h4>
                <div
                  class="slot-container outside-slot-container"
                  [class.hidden-slot]="!appointmentsAvailable.home"
                  [style.min-width]="appointmentsAvailable.slotWidth + 'px'"
                >
                  <div
                    [id]="getSlotId(appointmentsAvailable.home)"
                    class="slot slot-home sc-design-title-card"
                    tabindex="0"
                    role="button"
                    [attr.aria-label]="
                      'GlobalApplicativeConstants.accessibility.labels.outsideSlotBtn'
                        | translate
                          : {
                              apmtTime:
                                appointmentsAvailable.home?.time
                                | parseZoneTimeString
                                  : timezoneService.getTimezone(
                                      appointmentsAvailable.home
                                    )
                                | localeDate: 'shortTime'
                                | async
                            }
                    "
                    *ngIf="appointmentsAvailable.home"
                    (click)="onSlotClick(appointmentsAvailable.home)"
                    (keydown.enter)="onSlotClick(appointmentsAvailable.home)"
                    (keydown.space)="onSlotClick(appointmentsAvailable.home)"
                    [ngClass]="{
                      selected:
                        !!selectedAppointment &&
                        appointmentsAvailable.home?.id ===
                          selectedAppointment?.id
                    }"
                  >
                    <span class="slot-text">
                      {{
                        appointmentsAvailable.home?.time
                          | parseZoneTimeString
                            : timezoneService.getTimezone(
                                appointmentsAvailable.home
                              )
                          | localeDate: 'shortTime'
                          | async
                      }}
                    </span>
                    <span class="on-select-icon" *ngIf="!!selectedAppointment">
                      <sc-icon name="check_bis"></sc-icon>
                    </span>
                  </div>
                </div>
              </ng-container>
            </div>
          </li>
        </ol>
      </div>
      <div
        class="line-separator"
        *ngIf="lines && linesDisplayed.length > aptLines + 1"
      ></div>
    </div>
    <div
      class="half-day-home-appointments"
      *ngIf="
        displayHomeAppointments &&
        !displayOnlyReplacementVehicle &&
        displayHomeAppointmentsAsHalfDay &&
        (morningSlot || afternoonSlot)
      "
    >
      <div class="half-day-slots-label">
        <h4 class="inline-slot-label sc-design-text-4-bold">
          {{
            'CustomerDeclaration.AppointmentSearchPage.outsideIntervention'
              | translate
          }}
        </h4>
      </div>
      <div class="half-day-apmt-container sc-design-title-card">
        <sc-icon
          name="home"
          specificName="search_home"
          [forceInitialColor]="true"
        ></sc-icon>

        <ol class="slot-list-container" [attr.aria-labelledby]="ariaLabelledby">
          <li
            *ngIf="morningSlot"
            [id]="getSlotId(morningSlot.home)"
            class="half-day-list-slot slot"
            [ngClass]="{
              selected:
                !!selectedAppointment &&
                morningSlot.home?.id === selectedAppointment?.id
            }"
            (click)="onSlotClick(morningSlot.home)"
            (keydown.enter)="onSlotClick(morningSlot.home)"
            (keydown.space)="onSlotClick(morningSlot.home)"
          >
            <div>
              <span>{{
                'GlobalApplicativeConstants.time.morning' | translate
              }}</span>
              <span class="on-select-icon" *ngIf="!!selectedAppointment">
                <sc-icon name="check_bis"></sc-icon>
              </span>
            </div>
          </li>
          <li
            *ngIf="afternoonSlot"
            [id]="getSlotId(afternoonSlot.home)"
            class="half-day-list-slot slot"
            [ngClass]="{
              selected:
                !!selectedAppointment &&
                afternoonSlot.home?.id === selectedAppointment?.id
            }"
            (click)="onSlotClick(afternoonSlot.home)"
            (keydown.enter)="onSlotClick(afternoonSlot.home)"
            (keydown.space)="onSlotClick(afternoonSlot.home)"
          >
            <div>
              <span>{{
                'GlobalApplicativeConstants.time.afternoon' | translate
              }}</span>
              <span class="on-select-icon" *ngIf="!!selectedAppointment">
                <sc-icon name="check_bis"></sc-icon>
              </span>
            </div>
          </li>
        </ol>
      </div>
    </div>
    <div class="common-time-lines-actions">
      <sc-button
        *ngIf="!hasAllAppointmentLinesDisplayed()"
        class="show-more-slots-button"
        buttonType="mainWhite"
        [title]="
          'AppointmentCommonTimeLines.seeMoreSlotsTitle'
            | translate
              : {
                  fitterName: getFitterName(),
                  day: _appointments[0]?.time | localeDate: 'fullDate' | async
                }
        "
        (click)="onShowMoreSlotsClick()"
        (keydown.enter)="onShowMoreSlotsClick()"
        (keydown.space)="onShowMoreSlotsClick()"
      >
        {{ 'AppointmentCommonTimeLines.seeMoreSlotsLabel' | translate }}
      </sc-button>

      <ng-content select="[additionalAction]"></ng-content>
    </div>
  `,
})
export class AppointmentCommonTimeLinesComponent implements AfterViewInit {
  @Input() set appointments(appointments: Appointment[]) {
    if (appointments.length !== this._appointments?.length) {
      this._appointments = appointments;
      this.generateSlots();
    }
  }

  _appointments;
  @Input() fitter: Fitter;
  @Input() displayHomeAppointments = true;
  @Input() displayHomeAppointmentsAsHalfDay = true;
  @Input() displayGarageAppointments = true;
  @Input() displayReplacementVehicle = true;
  @Input() displayOnlyReplacementVehicle = true;
  @Input() ariaLabelledby?: string;

  @Output() slotClickEvent = new EventEmitter<Appointment>();

  linesDisplayed: Array<number> = [0];
  lines: AppointmentByDatetime[][] = [];
  freeSlotsWidths: FreeSlotsWidths = {
    beforeTenOClock: this.freeSlotWidth(
      this.localeDatePipe.getInstantTransform(
        new Date(2020, 10, 1, 7),
        'shortTime'
      )
    ),
    afterTenOClockAndBeforeNoon: this.freeSlotWidth(
      this.localeDatePipe.getInstantTransform(
        new Date(2020, 10, 1, 10),
        'shortTime'
      )
    ),
    noon: this.freeSlotWidth(
      this.localeDatePipe.getInstantTransform(
        new Date(2020, 10, 1, 12),
        'shortTime'
      )
    ),
    afternoonWidth: this.freeSlotWidth(
      this.localeDatePipe.getInstantTransform(
        new Date(2020, 10, 1, 14),
        'shortTime'
      )
    ),
    midnightWidth: this.freeSlotWidth(
      this.localeDatePipe.getInstantTransform(
        new Date(2020, 10, 1, 24),
        'shortTime'
      )
    ),
  };

  appointmentsGroupedByDatetime: Array<AppointmentByDatetime>;
  morningSlot: AppointmentByDatetime;
  afternoonSlot: AppointmentByDatetime;
  @Input() selectedAppointment: Appointment;

  cachedWindowWidth;
  resize$ = new Subject<void>();
  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.resize$.next();
  }

  constructor(
    private readonly el: ElementRef<HTMLElement>,
    public timezoneService: TimezoneService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly localeDatePipe: LocaleDatePipe,
    private readonly zone: NgZone,
    private readonly configurationService: ConfigurationService,
    private readonly translatorService: TranslatorService,
    private readonly focusManager: FocusManagerService,
    private readonly sandboxService: SandboxService,
    @Inject(CLIENT_APP_ID) private readonly clientAppId: ClientAppId
  ) {
    this.generateSlots = throttle(this.generateSlots, 2000, {
      leading: true,
      trailing: true,
    });
  }

  ngAfterViewInit() {
    this.cachedWindowWidth = window?.outerWidth;
    this.resize$.pipe(debounceTime(100)).subscribe(() => {
      if (this.cachedWindowWidth !== window?.outerWidth) {
        this.cachedWindowWidth = window?.outerWidth;
        this.generateFreeSlotLines();
        this.onShowMoreSlotsClick(true);
      }
    });
  }

  generateFreeSlotLines() {
    this.linesDisplayed = [0];
    this.lines = [];

    // Container width - Panel padding - Slot type icon
    const containerWidth =
      (document.getElementsByClassName('appointment-list')[0] as HTMLElement)
        ?.offsetWidth -
      48 -
      30;

    let currentLine: AppointmentByDatetime[] = [];
    let containerWidthLeft = containerWidth;
    for (const appointment of this.appointmentsGroupedByDatetime) {
      let freeSlotWidth = 20; // Slot padding
      if (isTimeBetween('00:01', '09:59', appointment.time)) {
        freeSlotWidth += this.freeSlotsWidths.beforeTenOClock;
      } else if (isTimeBetween('10:00', '11:59', appointment.time)) {
        freeSlotWidth += this.freeSlotsWidths.afterTenOClockAndBeforeNoon;
      } else if (isTimeBetween('12:00', '12:00', appointment.time)) {
        freeSlotWidth += this.freeSlotsWidths.noon;
      } else if (isTimeBetween('12:01', '23:59', appointment.time)) {
        freeSlotWidth += this.freeSlotsWidths.afternoonWidth;
      } else {
        freeSlotWidth += this.freeSlotsWidths.midnightWidth;
      }

      if (!this.displayHomeAppointmentsAsHalfDay || !!appointment.garage) {
        if (containerWidthLeft - freeSlotWidth < 0) {
          this.lines.push([...currentLine]);
          currentLine = [];
          containerWidthLeft = containerWidth;
        }

        currentLine.push({ ...appointment, slotWidth: freeSlotWidth });
        containerWidthLeft -= freeSlotWidth + 8; // Slot margin
      }
    }
    this.lines.push([...currentLine]);
    this.changeDetectorRef.detectChanges();
  }

  freeSlotWidth(text: string): number {
    let result;

    this.zone.runOutsideAngular(() => {
      const el = document.createElement('div');
      el.style.position = 'absolute';
      el.style.left = '-999em';
      el.style.whiteSpace = 'nowrap';
      el.style.fontFamily = 'var(--font-family-main)';
      el.style.fontSize = '14px';
      el.style.padding = '0px 8px';

      el.innerHTML = text;

      document.body.appendChild(el);

      result = el.clientWidth;

      if (this.clientAppId === 'customer') {
        result +=
          this.configurationService.getConfig()?.themeOptions?.customer
            ?.addedSlotWidth ?? 0;
      }

      document.body.removeChild(el);
    });

    return result;
  }

  generateSlots() {
    this.appointmentsGroupedByDatetime = Array.from(
      AppointmentUtils.getAppointmentsGroupedByDatetime(this._appointments),
      ([date, appointments]) => {
        if (appointments) {
          const appointmentHome = appointments.find(
            (appointment) => appointment.outsideIntervention
          );
          const appointmentGarage = appointments.find(
            (appointment) => !appointment.outsideIntervention
          );
          return {
            home: appointmentHome,
            garage: appointmentGarage,
            time: appointmentHome
              ? appointmentHome.time
              : appointmentGarage.time,
            slotWidth: 0,
          };
        }
      }
    ).filter(
      (appointmentsOfDatetime) =>
        !!appointmentsOfDatetime.garage || !!appointmentsOfDatetime.home
    );

    this.morningSlot = this.appointmentsGroupedByDatetime
      .filter((slot) => slot?.home)
      .find(
        (appointment) =>
          dayjs(appointment?.time)
            .tz(this.timezoneService.getTimezone(appointment.home))
            .format('HH:mm') <
          this.configurationService.getBrandMobilityHalfDayLimit(
            appointment?.home?.brandId
          )
      );

    this.afternoonSlot = this.appointmentsGroupedByDatetime
      .filter((slot) => slot?.home)
      .find(
        (appointment) =>
          dayjs(appointment?.time)
            .tz(this.timezoneService.getTimezone(appointment.home))
            .format('HH:mm') >=
          this.configurationService.getBrandMobilityHalfDayLimit(
            appointment?.home?.brandId
          )
      );

    // some delay is added here because of container animation
    // in order to get the final container width
    of(true)
      .pipe(delay(500))
      .subscribe(() => {
        this.generateFreeSlotLines();
        this.onShowMoreSlotsClick(true);
      });
  }

  getSlotId(appointment: Appointment) {
    return this.sandboxService.formatSlotElementId(appointment);
  }

  onSlotClick(appointment: Appointment) {
    this.slotClickEvent.emit(appointment);
  }

  onShowMoreSlotsClick(isInit = false) {
    const existingLinesId: string[] = [];
    this.el.nativeElement
      .querySelectorAll('[id|="time-line"]')
      .forEach((elemFound) => existingLinesId.push(elemFound.id));

    let linesToAdd = 2;

    while (
      this.lines?.length > 1 &&
      !this.hasAllAppointmentLinesDisplayed() &&
      (!isInit || linesToAdd > 0)
    ) {
      this.linesDisplayed.push(this.linesDisplayed.length);
      linesToAdd--;
    }

    if (!isInit) {
      setTimeout(() => {
        let firstNewLine: HTMLElement = null;
        this.el.nativeElement.childNodes.forEach((childNode: HTMLElement) => {
          if (!firstNewLine && !existingLinesId.includes(childNode.id)) {
            firstNewLine = childNode;
          }
        });

        this.focusManager.focusFirstElementAllowed(firstNewLine, true);
      }, 0);
    }
  }

  isLineHasAppointmentAtGarage(
    appointmentsOfLine: Array<AppointmentByDatetime>
  ) {
    return (
      appointmentsOfLine?.filter((appointmentsAtDatetime) => {
        return !!appointmentsAtDatetime.garage;
      }).length > 0
    );
  }

  isLineHasAppointmentAtHome(appointmentsOfLine: Array<AppointmentByDatetime>) {
    return (
      appointmentsOfLine?.filter((appointmentsAtDatetime) => {
        return !!appointmentsAtDatetime.home;
      }).length > 0
    );
  }

  hasAllAppointmentLinesDisplayed(): boolean {
    if (!this.displayHomeAppointmentsAsHalfDay) {
      return this.linesDisplayed.length === this.lines.length;
    }
    if (this.displayGarageAppointments) {
      for (let i = this.linesDisplayed.length; i < this.lines.length; i++) {
        if (this.lines[i].find((appointment) => appointment?.garage)) {
          return false;
        }
      }
    }
    return true;
  }

  getFitterName() {
    return !!this.fitter?.brand || !!this.fitter?.city
      ? `${this.fitter?.brand} ${this.fitter?.city}`
      : this.translatorService.getInstantTranslation(
          'AppointmentEarliestList.unknownFitter'
        );
  }
}

interface AppointmentByDatetime {
  home: Appointment;
  garage: Appointment;
  time: string | Date;
  slotWidth: number;
}

interface FreeSlotsWidths {
  beforeTenOClock: number;
  afterTenOClockAndBeforeNoon: number;
  noon: number;
  afternoonWidth: number;
  midnightWidth: number;
}
